import { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EmojiType } from "../../../models/local/Types";
import { cn } from "../../../lib/utils";
import { Button } from "../../../components/ui/button";

const MsgEmojiSelector: React.FC<{
  show: boolean;
  setShow: (v: boolean) => void;
  onSelect: (e: EmojiType) => void;
}> = ({ show, setShow, onSelect }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [footerDimensions, setFooterDimensions] = useState<{ width: number; left: number } | null>(null);

  const EmojiItem: FC<{ e: EmojiType }> = ({ e }) => {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => select(e)}
        className="w-20 h-20 p-0 hover:bg-accent/50 hover:scale-105 active:scale-95 active:bg-accent rounded-lg transition-all duration-200"
        aria-label={`Select ${e} emoji`}
      >
        <img
          src={`/assets/emoji/${e}.png`}
          alt={`${e} emoji`}
          className="w-20 h-20"
        />
      </Button>
    );
  };

  const select = (e: EmojiType) => {
    onSelect(e);
    handleClose();
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShow(false);
      setIsClosing(false);
    }, 300); // 애니메이션 duration과 동일
  };

  // Footer 너비 측정 및 화면 크기 변경 감지
  useEffect(() => {
    const updateDimensions = () => {
      const footer = document.querySelector('#msg-input-footer-container');
      if (footer) {
        const rect = footer.getBoundingClientRect();
        setFooterDimensions({
          width: rect.width,
          left: rect.left
        });
      }
    };

    if (show) {
      // 초기 측정
      updateDimensions();

      // 화면 크기 변경 감지 (회전, 리사이즈 등)
      window.addEventListener('resize', updateDimensions);

      return () => {
        window.removeEventListener('resize', updateDimensions);
      };
    }
  }, [show]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        handleClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [show]);

  if (!show && !isClosing) return null;

  const content = (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-[1900]",
          isClosing ? "animate-out fade-out-0 duration-300" : "animate-in fade-in-0"
        )}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        className={cn(
          "fixed bottom-0 z-[2000] bg-background rounded-t-2xl border-t border-l border-r shadow-lg",
          isClosing
            ? "animate-out slide-out-to-bottom duration-300"
            : "animate-in slide-in-from-bottom duration-300"
        )}
        style={footerDimensions ? {
          width: `${footerDimensions.width}px`,
          left: `${footerDimensions.left}px`
        } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label="Emoji selector"
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-muted-foreground/20 rounded-full" />
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[400px] px-5 pb-10">
          <div className="grid grid-cols-4 gap-2.5 justify-items-center sm:grid-cols-6 md:grid-cols-8">
            <EmojiItem e='hi' />
            <EmojiItem e='hello' />
            <EmojiItem e='best' />
            <EmojiItem e='ok' />
            <EmojiItem e='cheer' />
            <EmojiItem e='thanks' />
            <EmojiItem e='please' />
            <EmojiItem e='heart' />
            <EmojiItem e='knock' />
            <EmojiItem e='wait' />
            <EmojiItem e='hmm' />
            <EmojiItem e='tada' />
            <EmojiItem e='soldier' />
            <EmojiItem e='fire' />
            <EmojiItem e='asap' />
            <EmojiItem e='superpower' />
            <EmojiItem e='run' />
            <EmojiItem e='gogo' />
            <EmojiItem e='sitandsweat' />
            <EmojiItem e='where' />
            <EmojiItem e='donut' />
            <EmojiItem e='sofa' />
            <EmojiItem e='cake' />
            <EmojiItem e='handsup' />
            <EmojiItem e='fishing' />
            <EmojiItem e='busy' />
            <EmojiItem e='huh' />
            <EmojiItem e='letsbreak' />
            <EmojiItem e='bored' />
            <EmojiItem e='hunch' />
            <EmojiItem e='scared' />
            <EmojiItem e='goodnight' />
            <EmojiItem e='bye' />
          </div>
        </div>
      </div>
    </>
  );

  // Portal to body to avoid parent positioning issues
  return createPortal(content, document.body);
};

export default MsgEmojiSelector;
